import { trace, getRandomInt } from 'rytm-helpers';
import * as THREE from 'three';
import Stats from 'three/addons/libs/stats.module.js';

import { 
  playBgSounds, 
  playAccentSound, 
  fadeOutBgSounds, 
  fadeOutSounds, 
  fadeOutAccentSounds, 
  stopAllSounds, 
  getAccentSoundAnalyserData,
} from './mixer';
import { 
  getBackgroundSoundParams, 
  setAccentSoundPot, 
  getRandomAccentParams,
  getPauseTime,
  getAccentLayers,
} from './generative_audio';
import { 
  setCompositionParams,
  getAccentShapes,
} from './generative_shapes';
import {
  FPS,
  HIDE_PLAYGROUND_TIME,
  SHOW_STATS,
} from './config';

const CLOCK_INTERVAL = 1 / FPS

let index = 0;
let audioLayers = 0;
let params, wrapper, scene, renderer, camera, stats, hideTimeout, updateCanvasSizeTimeout, clock, clock_delta;
let pauseTimeouts = [];
let shapes = [];

// ### INIT playground - prepare scene etc. ###
// on complete - from within controllers/HomePlayController 
// (moves to the net playground)
export const initPlayground = (onComplete = null) => {
  trace("init playground");
  // init wrapper and params
  wrapper = document.querySelector('.editionhome-playground');
  params = wrapper ? JSON.parse(wrapper.dataset.playgroundParams) : null;
  // trace(params)
  // init THREE scene
  initScene();
  // init clock
  clock = new THREE.Clock();
  clock_delta = 0;
  animate();
  // add event listeners
  window.addEventListener('resize', updateCanvasSize);
  // invoke callback after delay
  clearTimeout(hideTimeout);
  hideTimeout = setTimeout(() => {
    if (onComplete) {
      onComplete();
    }
  }, HIDE_PLAYGROUND_TIME);
}
// ### next playground ###
// prepare and start new composition 
// start bg sound, and start accent sounds sequence 
// along with shapes animation
export const nextPlayground = () => {
  // trace("next playground");
  // play random bg sounds
  playBgSounds(getBackgroundSoundParams(params.audio_loops));
  // set accent sounds pot (bank)
  setAccentSoundPot(params.audio_accents);
  // randomize audio layers
  audioLayers = getAccentLayers();
  // trace("audio accent layers:", audioLayers);
  // start accent sounds sequence
  startAccentSounds();
  // start animation
  // set shapes params
  setCompositionParams( getViewportRect() );
  // composition index +1 
  index++;
}
// ### STOP (remove event listeners, etc.)
// playground is still visible. But event listeners should be removed
// fade out sound + start hide animation of all shapes
export const stopPlayground = (onComplete = null) => {
  trace("stop playground");
  // stop pause timeouts
  stopPauseTimeouts();
  // fade out sounds
  fadeOutBgSounds(HIDE_PLAYGROUND_TIME * 1.2);
  fadeOutAccentSounds();
  // hide old shapes
  animationHide();
  clearTimeout(hideTimeout);
  hideTimeout = setTimeout(() => {
    if (onComplete) {
      onComplete();
    }
  }, HIDE_PLAYGROUND_TIME);
}
// ### DESTROY ###
// playground nolonger visible
export const destroyPlayground = () => {
  trace("destroy playground");
  // stop pause timeouts
  stopPauseTimeouts();
  // stop all sounds
  stopAllSounds();
  // reset values
  wrapper = null;
  params = null;
  clock = null;
  clock_delta = 0
  audioLayers = 0;
  index = 0;
  // destroy scene
  destroyScene();
  window.removeEventListener('resize', updateCanvasSize);
}
// stop all pause timeouts
// pause timeouts are timeouts used to have random
// silence between accent sounds
const stopPauseTimeouts = () => {
  pauseTimeouts.forEach((timeout) => {
    clearTimeout(timeout);
  });
}
// ### Accent sounds sequence ###
// start accent sound for each audio accent LAYER
const startAccentSounds = () => {
  for (let i = 0; i < audioLayers; i++) {
    // the first pause hsould be not to long
    playRandomAccent(i == 0 ? HIDE_PLAYGROUND_TIME * .5 : null);
  }
}
// start with a random pause and then play random accent sound
const playRandomAccent = (pauseTime = null) => {
  pauseTime = pauseTime ? pauseTime : getPauseTime();
  let timeout = setTimeout(() => {
    const params = getRandomAccentParams();
    const currentIndex = index + 0;
    playAccentSound({
      ...params,
      onPlay: () => {
        // invoke visuals
        onAccentStart(params);
      },
      onComplete: () => {
        // check if composition changed
        if (currentIndex == index) {
          onAccentStop(params);
          playRandomAccent();
        }
      },
    });
  }, pauseTime);
  pauseTimeouts.push(timeout);
}
/**
 * ### Visual Animation ###
 */
// create shapes on accent audio start
const onAccentStart = (params) => {
  // trace("accent START", params);
  const accShapes = getAccentShapes(params, getViewportRect());
  // add shapes to scene
  accShapes.forEach((s) => {
    shapes.push(s);
    scene.add(s.getObject());
    s.show();
  });

}
// on accent audio stop
const onAccentStop = (params) => {
  // trace("accent STOP", params, shapes.length);
}
// animate FPS
const animate = () => {
  if (clock && renderer && scene && camera) {
    requestAnimationFrame(animate);
    // FPS
    clock_delta += clock.getDelta();
    if (clock_delta > CLOCK_INTERVAL) {
      // Update objects
      updateObjects();
      // Render scene
      renderer.render(scene, camera);
      clock_delta = clock_delta % CLOCK_INTERVAL;
      // Update stats
      if (SHOW_STATS && stats) {
        stats.update();
      }
    }
  }
}
// update on animation frame (draw shapes)
const updateObjects = () => {
  shapes.forEach((shape) => {
    // check status update / remove
    if (shape.visible) {
      shape.update();
    } else {
      removeShape(shape);
    }
  });
}

// init scene
const initScene = () => {
  if (!wrapper) {
    console.warn("Playground wrapper not found (init)");
    return;
  }
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera( 45, window.innerWidth / window.innerHeight, 1, 500 );
  camera.position.set( 0, 0, 100 );
  camera.lookAt( 0, 0, 0 );
  renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  wrapper.appendChild( renderer.domElement );
  updateCanvasSize(0);
  // tmp stats
  if (SHOW_STATS && !stats) {
    stats = new Stats();
    wrapper.appendChild( stats.dom );
  }
}

// update canvas size
const updateCanvasSize = ( delay = 200) => {
  clearTimeout(updateCanvasSizeTimeout);
  updateCanvasSizeTimeout = setTimeout(() => {
    // trace("update canvas size");
    if (wrapper && renderer && camera) {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize( wrapper.offsetWidth, wrapper.offsetHeight );
    }
  }, delay);
}
// destroy scene
const destroyScene = () => {
  if (!wrapper) {
    console.warn("Playground wrapper not found (destroy)");
    return;
  }
  clearScene();
  scene = null
  if (renderer) {
    renderer.domElement.remove()
    renderer = null
  }
  camera = null
}
// clear scene
const clearScene = () => {
  if (!scene) {
    console.warn("Playground scene not found (clear)");
    return;
  }
  scene.children.forEach((child) => {
    scene.remove(child);
  });
}

const animationHide = () => {
  shapes.forEach((shape) => {
    shape.hide();
  });
}
// on shape life end
const removeShape = (shape) => {
  let shapesTmp = [];
  shapes.forEach((s) => {
    if (s.params.id == shape.params.id) {
      // remove from scene
      scene.remove(s.getObject());
      s.destroyObject();
    } else {
      shapesTmp.push(s);
    }
  });
  shapes = shapesTmp;
}
// THREE.js viewport rect (width, height)
const getViewportRect = () => {
  let rect = {
    width: 0,
    height: 0,
  };
  if (camera) {
    const vFov = THREE.MathUtils.degToRad( camera.fov ); 
    const height = 2 * Math.tan( vFov / 2 ) * camera.position.z;
    const width = height * camera.aspect;
    rect.width = width;
    rect.height = height;
  }
  return rect;
}



